/**
 * Error boundary. Without one, any exception thrown during render unmounts the
 * whole React tree and leaves a blank white screen. This catches render errors
 * for the routed view and shows a small recoverable fallback instead. Wrapped
 * with a `key={route}` in App so navigating to another tab clears the error.
 */
import { Component, type ReactNode } from "react";
import { Icon } from "./icons";

type Props = { children: ReactNode; onReset?: () => void };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    // Surface it in the console for debugging; the UI shows the fallback below.
    console.error("Render error caught by ErrorBoundary:", error);
  }

  private reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center", padding: 30 }}>
        <div style={{ textAlign: "center", maxWidth: 420 }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%", margin: "0 auto 18px",
            display: "grid", placeItems: "center",
            background: "color-mix(in oklch, var(--bad) 12%, transparent)", color: "var(--bad)",
          }}>
            <Icon name="shield" size={30} />
          </div>
          <div className="display" style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", marginBottom: 10 }}>
            Something went wrong on this screen
          </div>
          <p style={{ color: "var(--ink-3)", fontSize: 14.5, lineHeight: 1.5, marginBottom: 18 }}>
            The rest of the app is fine — you can retry this screen or switch tabs.
          </p>
          <button onClick={this.reset} style={{
            padding: "10px 20px", borderRadius: "var(--r-pill)", border: "1px solid var(--border)",
            background: "var(--accent)", color: "var(--on-accent)", fontWeight: 600, cursor: "pointer",
          }}>
            Try again
          </button>
        </div>
      </div>
    );
  }
}
